import { Link } from "react-router-dom";
import { Facebook, Instagram, Lock, Mail, MapPin, Phone, Star } from "lucide-react";
import { site, fullAddress } from "@/config/site";
import { products } from "@/data/products";
import { trackEvent } from "@/lib/tracking";

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 1 1-2.31-2.83V9.32a6.34 6.34 0 1 0 5.76 6.35V8.69a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.12z" />
  </svg>
);

const columns = [
  {
    title: "My Blinds Company",
    links: [
      { label: "About Us", to: "/about" },
      { label: "Why Choose Us", to: "/why-choose-us" },
      { label: "Our Work", to: "/our-work" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    title: "Services",
    links: [
      { label: "Free Home Measure", to: "/free-home-measure" },
      { label: "Blinds in Birmingham", to: "/blinds-birmingham" },
      { label: "Commercial Blinds", to: "/commercial-blinds-birmingham" },
    ],
  },
  {
    title: "Blinds",
    links: products.map((p) => ({
      label: p.name.replace(" Blinds", "").replace(" & Thermal", " & Thermal"),
      to: p.slug === "shutters" ? "/shutters" : `/${p.slug}`,
    })),
  },
  {
    title: "Shutters",
    links: [
      { label: "Shutters", to: "/shutters" },
      { label: "Shutter Consultation", to: "/shutters" },
    ],
  },
  {
    title: "Help",
    links: [
      { label: "FAQs", to: "/#faqs" },
      { label: "Areas We Cover", to: "/areas-we-cover" },
      { label: "Privacy Policy", to: "/privacy-policy" },
      { label: "Terms & Conditions", to: "/terms-and-conditions" },
    ],
  },
];

export function Footer() {
  return (
    <footer className="bg-navy-950 text-white/80" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-28 md:pb-16">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">
          {columns.map((col) => (
            <nav key={col.title} aria-label={col.title}>
              <h3 className="font-serif text-lg text-white mb-4">{col.title}</h3>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label + l.to}>
                    <Link
                      to={l.to}
                      data-testid={`footer-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="text-sm text-white/60 hover:text-white transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
          <div className="col-span-2 md:col-span-4 lg:col-span-1">
            <img src="/logo.png" alt="My Blinds Company logo" className="h-12 w-auto rounded mb-5 bg-white p-1" />
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-white/50" />
                <span>{fullAddress}</span>
              </li>
              <li>
                <a
                  href={site.phone.href}
                  data-testid="footer-phone-link"
                  onClick={() => trackEvent("phone_click", { location: "footer" })}
                  className="flex items-center gap-2.5 hover:text-white transition-colors"
                >
                  <Phone className="h-4 w-4 shrink-0 text-white/50" />
                  {site.phone.display}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${site.email}`}
                  data-testid="footer-email-link"
                  onClick={() => trackEvent("email_click", { location: "footer" })}
                  className="flex items-center gap-2.5 hover:text-white transition-colors break-all"
                >
                  <Mail className="h-4 w-4 shrink-0 text-white/50" />
                  {site.email}
                </a>
              </li>
            </ul>
            <div className="mt-5">
              <p className="text-xs uppercase tracking-[0.18em] text-white/40 mb-2">Opening hours</p>
              <ul className="space-y-1 text-sm text-white/60">
                {site.hours.map((h) => (
                  <li key={h.days} className="flex justify-between gap-4">
                    <span>{h.days}</span>
                    <span className="text-white/80">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex items-center gap-3 mt-6">
              {site.social.facebook && (
                <a href={site.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 hover:bg-white/10 transition-colors">
                  <Facebook className="h-4 w-4" />
                </a>
              )}
              {site.social.instagram && (
                <a href={site.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 hover:bg-white/10 transition-colors">
                  <Instagram className="h-4 w-4" />
                </a>
              )}
              {site.social.tiktok && (
                <a href={site.social.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="footer-tiktok" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 hover:bg-white/10 transition-colors">
                  <TikTokIcon className="h-4 w-4" />
                </a>
              )}
              {site.social.googleReviews && (
                <a href={site.social.googleReviews} target="_blank" rel="noopener noreferrer" aria-label="Google reviews" data-testid="footer-google-reviews" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 hover:bg-white/10 transition-colors">
                  <Star className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="mt-14 border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/40">
          <p>© {new Date().getFullYear()} {site.legalName}. All rights reserved.</p>
          <p className="flex items-center gap-5">
            <span>Made-to-measure blinds & shutters, Birmingham.</span>
            <Link to="/portal/login" data-testid="footer-portal-link" className="inline-flex items-center gap-1.5 text-white/60 hover:text-white font-medium transition-colors">
              <Lock className="h-3 w-3" /> Staff Login
            </Link>
          </p>
        </div>
      </div>
    </footer>
  );
}
